/**
 * Store availability checker
 * Fetches and displays product stock levels across store locations
 */

class StoreAvailability {
  constructor(productHandle, variantId) {
    this.productHandle = productHandle;
    this.variantId = variantId;
    this.modal = null;
    this.googleMapsApiKey = null; // Will be set via config
  }

  /**
   * Set Google Maps API key for geocoding
   * @param {string} apiKey - Google Maps API key
   */
  setApiKey(apiKey) {
    this.googleMapsApiKey = apiKey;
  }

  /**
   * Calculate distance between two coordinates using Haversine formula
   * @param {number} lat1 - Latitude of point 1
   * @param {number} lon1 - Longitude of point 1
   * @param {number} lat2 - Latitude of point 2
   * @param {number} lon2 - Longitude of point 2
   * @returns {number} Distance in kilometers
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  /**
   * Geocode an address using Google Maps Geocoding API
   * @param {string} address - Address to geocode
   * @returns {Promise<{lat: number, lng: number}|null>}
   */
  async geocodeAddress(address) {
    if (!this.googleMapsApiKey) {
      console.error('Google Maps API key not set. Call setApiKey() first.');
      return null;
    }

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&region=au&key=${this.googleMapsApiKey}`
      );
      const data = await response.json();

      if (data.status === 'OK' && data.results.length > 0) {
        return data.results[0].geometry.location;
      } else {
        console.error('Geocoding failed:', data.status);
        return null;
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
      return null;
    }
  }

  /**
   * Find and highlight the closest store to user's location
   * @param {string} userLocation - User's suburb or postcode
   */
  async findClosestStore(userLocation) {
    const userCoords = await this.geocodeAddress(userLocation);

    if (!userCoords) {
      alert('Could not find that location. Please try a different suburb or postcode.');
      return;
    }

    const storeElements = this.modal.content.querySelectorAll('[data-store-location]');
    const storesWithDistances = [];

    // Calculate distances for all stores
    for (const storeEl of storeElements) {
      const storeAddress = storeEl.dataset.storeAddress;
      const storeName = storeEl.dataset.storeName;

      const storeCoords = await this.geocodeAddress(storeAddress);

      if (storeCoords) {
        const distance = this.calculateDistance(
          userCoords.lat,
          userCoords.lng,
          storeCoords.lat,
          storeCoords.lng
        );

        storesWithDistances.push({
          element: storeEl,
          distance: distance,
          name: storeName
        });
      }
    }

    if (storesWithDistances.length === 0) {
      alert('Could not calculate distances to stores.');
      return;
    }

    // Sort by distance
    storesWithDistances.sort((a, b) => a.distance - b.distance);

    // Remove previous highlights and distances
    storeElements.forEach(el => {
      el.classList.remove('is-closest');
      const existingDistance = el.querySelector('.StoreLocation-distance');
      if (existingDistance) {
        existingDistance.remove();
      }
    });

    // Highlight closest store and add distances
    storesWithDistances.forEach((store, index) => {
      if (index === 0) {
        store.element.classList.add('is-closest');
      }

      // Add distance badge
      const distanceBadge = document.createElement('div');
      distanceBadge.className = 'StoreLocation-distance';
      distanceBadge.textContent = `${store.distance.toFixed(1)} km away`;

      const nameEl = store.element.querySelector('.StoreLocation-name');
      if (nameEl) {
        nameEl.insertAdjacentElement('afterend', distanceBadge);
      }
    });

    // Scroll closest store into view
    storesWithDistances[0].element.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }

  /**
   * Initialize event listeners for location search
   */
  initLocationSearch() {
    const searchInput = this.modal.content.querySelector('[data-location-search]');
    const searchBtn = this.modal.content.querySelector('[data-location-search-btn]');

    if (searchBtn && searchInput) {
      searchBtn.addEventListener('click', () => {
        const location = searchInput.value.trim();
        if (location) {
          searchBtn.textContent = 'Searching...';
          searchBtn.disabled = true;

          this.findClosestStore(location).finally(() => {
            searchBtn.textContent = 'Find Closest Store';
            searchBtn.disabled = false;
          });
        }
      });

      // Also allow Enter key
      searchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          searchBtn.click();
        }
      });
    }
  }

  async fetchAvailability() {
    try {
      // Fetch product page with the specific variant selected
      const response = await fetch(
        `/products/${this.productHandle}?variant=${this.variantId}&section_id=store-availability-drawer`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch store availability');
      }

      const html = await response.text();

      // Parse the section content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const availabilityContainer = doc.querySelector('.store-availability-container');

      if (availabilityContainer) {
        return availabilityContainer.innerHTML;
      }

      return null;
    } catch (error) {
      console.error('Error fetching store availability:', error);
      return null;
    }
  }

  async show() {
    this.modal = new Modal('store-availability-modal');

    // Show loading state
    this.modal.setContent(`
      <div class="StoreAvailability-loading">
        <p>Loading store availability...</p>
      </div>
    `);
    this.modal.open();

    // Fetch availability data
    const availabilityHtml = await this.fetchAvailability();

    if (availabilityHtml) {
      this.modal.setContent(availabilityHtml);
      // Initialize location search after content is loaded
      this.initLocationSearch();
    } else {
      // Fallback: Show message
      this.modal.setContent(`
        <div class="StoreAvailability-unavailable">
          <p>Store availability information is currently unavailable. Please contact us for stock information.</p>
        </div>
      `);
    }
  }
}

// Make it globally available
window.StoreAvailability = StoreAvailability;
